import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Settings, 
  LogOut,
  Flag,
  Upload,
  HelpCircle,
  FileSpreadsheet,
  Newspaper,
} from 'lucide-react';

// Import refactored components
import { AdminOverview } from '@/components/admin/AdminOverview';
import { AdminBusinesses } from '@/components/admin/AdminBusinesses';
import { AdminUsers } from '@/components/admin/AdminUsers';
import { AdminClaims } from '@/components/admin/AdminClaims';
import { AdminSettings } from '@/components/admin/AdminSettings';
import { AdminGuide } from '@/components/admin/AdminGuide';

const API = `${window.location.origin}/api`;

const LOGO_URL = "/blackified-logo.png";

// Main Admin Dashboard Component
export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    total_users: 0,
    total_businesses: 0,
    pending_businesses: 0,
    verified_businesses: 0,
    rejected_businesses: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/admin/stats`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      const { clearSessionToken, authFetch } = await import('../utils/authToken');
      await authFetch(`${API}/auth/logout`, {
        method: 'POST'
      });
      clearSessionToken();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/admin/businesses', icon: Building2, label: 'Businesses' },
    { path: '/admin/claims', icon: Flag, label: 'Claims' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
    { path: '/admin/ingestion', icon: Upload, label: 'AI Ingestion' },
    { path: '/admin/bulk-import', icon: FileSpreadsheet, label: 'Bulk Import' },
    { path: '/admin/blog', icon: Newspaper, label: 'Blog' },
    { path: '/admin/guide', icon: HelpCircle, label: 'Admin Guide' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-zinc-900 p-6">
        <Link to="/" className="mb-8">
          <img src={LOGO_URL} alt="Blackified" className="h-8 object-contain" />
        </Link>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-500 hover:text-white hover:bg-zinc-900/50'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-900/50 transition-colors"
          data-testid="admin-logout"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#050505] border-b border-zinc-900 flex items-center justify-between px-4 z-50">
        <Link to="/">
          <img src={LOGO_URL} alt="Blackified" className="h-6 object-contain" />
        </Link>
        <div className="flex items-center gap-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                isActive(item.path, item.exact)
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
            </Link>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-12 pt-20 md:pt-12 overflow-auto">
        <Routes>
          <Route index element={<AdminOverview stats={stats} />} />
          <Route path="businesses" element={<AdminBusinesses />} />
          <Route path="claims" element={<AdminClaims />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="guide" element={<AdminGuide />} />
        </Routes>
      </main>
    </div>
  );
}
